/* eslint react-hooks/preserve-manual-memoization: ["warn"] */
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "@coworker/locales";
import PersonIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/PersonIconPath";
import FixaSearch from "@coworker/apprestructured/src/shared/wrappers/FixaSearch/FixaSearch";
import { FixaSegmentedControl } from "@coworker/apprestructured/src/shared/wrappers/FixaSegmentedControl/FixaSegmentedControl";
import { TeamsList } from "./TeamsList/TeamsList";
import { UsersList } from "./UsersList/UsersList";
import ListItem from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/ListItem/ListItem";
import ErrorAddOn from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/ListItem/ErrorAddOn/ErrorAddOn";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import ModalColumn from "../ModalColumn/ModalColumn";
import StyledModalFooter from "../StyledModalFooter/StyledModalFooter";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import useSaveGuardedFunction from "@coworker/apprestructured/src/save-guard/useSaveGuardedFunction";
import { StoreTeam } from "@coworker/types/lib/storeteam";
import { User } from "@coworker/apprestructured/src/shared/types/user";
import styles from "./AssignTo.module.css";

type AssignToProps = {
  disabled: boolean;
  assignedUserId: string | null;
  assignedTeamId: string | null;
  description: string;
  searchQuery: string;
  matchingTeams: StoreTeam[];
  isTeamsLoading: boolean;
  myTeamId: string;
  matchingUsers: User[];
  isUsersLoading: boolean;
  teams: StoreTeam[];
  isError: boolean;
  onConfirmClick: ({
    assignedUserId,
    assignedTeamId,
  }: {
    assignedUserId: string | null;
    assignedTeamId: string;
  }) => void;
  onSearchQueryChange: (query: string) => void;
};

export const AssignTo = ({
  disabled,
  assignedUserId,
  assignedTeamId,
  description,
  searchQuery,
  matchingTeams,
  isTeamsLoading,
  myTeamId,
  matchingUsers,
  isUsersLoading,
  teams,
  isError,
  onConfirmClick,
  onSearchQueryChange,
}: AssignToProps) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [modalAssignedUserId, setModalAssignedUserId] = useState<string | null>(
    null
  );
  const [modalAssignedTeamId, setModalAssignedTeamId] = useState<string | null>(
    null
  );
  const [noSelectedItem, setNoSelectedItem] = useState(false);

  const { t } = useTranslation();

  const handleListViewItemClick = useCallback(() => {
    setShowModal(true);
    setModalAssignedUserId(assignedUserId);
    setModalAssignedTeamId(assignedUserId ? null : assignedTeamId);
    onSearchQueryChange("");
    setSelectedIndex(assignedUserId ? 1 : assignedTeamId ? 0 : 0);
    setNoSelectedItem(false);
  }, [assignedUserId, assignedTeamId, onSearchQueryChange]);

  const {
    saveGuardedFunction: handleCloseClick,
    enableSaveGuard,
    disableSaveGuard,
  } = useSaveGuardedFunction(
    useCallback(() => {
      setShowModal(false);
    }, [])
  );

  const handleModalUserIdChange = useCallback(
    (userId: string) => {
      setModalAssignedUserId(userId);
      setNoSelectedItem(false);
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const handleModalTeamIdChange = useCallback(
    (teamId: string) => {
      setModalAssignedTeamId(teamId);
      setNoSelectedItem(false);
      enableSaveGuard();
    },
    [enableSaveGuard]
  );

  const handleSearchForCoworkersClick = useCallback(() => {
    setSelectedIndex(1);
  }, []);

  const handleSearchForTeamsClick = useCallback(() => {
    setSelectedIndex(0);
  }, []);

  const handleClearSearchClick = useCallback(() => {
    onSearchQueryChange("");
  }, [onSearchQueryChange]);

  const handleSegmentedControlClick = useCallback((_: unknown, i: number) => {
    setSelectedIndex(i);
    setNoSelectedItem(false);
  }, []);

  const isTeamsSelected = selectedIndex === 0;
  const isCoworkersSelected = selectedIndex === 1;

  const handleConfirmClick = useCallback(() => {
    if (isTeamsSelected && !modalAssignedTeamId) {
      setNoSelectedItem(true);
      return;
    }
    if (isCoworkersSelected && !modalAssignedUserId) {
      setNoSelectedItem(true);
      return;
    }
    onConfirmClick({
      assignedTeamId: isCoworkersSelected
        ? matchingUsers.find((user) => user.uid === modalAssignedUserId)!.teamId
        : modalAssignedTeamId!,
      assignedUserId: isTeamsSelected ? null : modalAssignedUserId,
    });
    setShowModal(false);
    disableSaveGuard();
  }, [
    isTeamsSelected,
    modalAssignedTeamId,
    isCoworkersSelected,
    modalAssignedUserId,
    onConfirmClick,
    matchingUsers,
    disableSaveGuard,
  ]);

  const labels = useMemo<{ label: string; type: "button" }[]>(
    () => [
      { label: t("teamsString"), type: "button" },
      { label: t("coworkersString"), type: "button" },
    ],
    [t]
  );

  return (
    <>
      <ListItem
        title={t("assignString")}
        icon={PersonIconPath}
        onClick={handleListViewItemClick}
        disabled={disabled}
        addOn={
          isError ? <ErrorAddOn>{t("pleaseFillInString")}</ErrorAddOn> : null
        }
        description={description}
        emphasised={!!assignedTeamId || !!assignedUserId}
      />
      <FixaModal visible={showModal} handleCloseBtn={handleCloseClick}>
        <FixaSheets
          labelledById="task-management-assign-to-header"
          header={
            <FixaModalHeader
              id="task-management-assign-to-header"
              title={t("assignString")}
              closeBtnClick={handleCloseClick}
            />
          }
          footer={
            <StyledModalFooter>
              <div className={styles["footerWrapper"]}>
                {noSelectedItem && (
                  <ErrorAddOn>{t("pleaseFillInString")}</ErrorAddOn>
                )}
                <FixaButton
                  text={t("confirmString")}
                  onClick={handleConfirmClick}
                  type="primary"
                />
              </div>
            </StyledModalFooter>
          }
          fullSize
        >
          <FixaModalBody>
            <ModalColumn gap="medium">
              <FixaSegmentedControl
                items={labels}
                activeIndex={selectedIndex}
                size="medium"
                onClick={handleSegmentedControlClick}
              />
              <FixaSearch
                id="assign-to-search"
                value={searchQuery}
                placeholder={t(
                  `${isTeamsSelected ? "searchTeamString" : "searchCoworkerString"}`
                )}
                onChange={(e) => {
                  onSearchQueryChange(e.target.value);
                }}
                onClear={handleClearSearchClick}
              />
              {isTeamsSelected && (
                <TeamsList
                  matchingTeams={matchingTeams}
                  isLoading={isTeamsLoading}
                  myTeamId={myTeamId}
                  searchQuery={searchQuery}
                  assignedTeamId={modalAssignedTeamId}
                  onTeamIdChange={handleModalTeamIdChange}
                  onSearchForCoworkersClick={handleSearchForCoworkersClick}
                  onClearSearchClick={handleClearSearchClick}
                />
              )}
              {isCoworkersSelected && (
                <UsersList
                  matchingUsers={matchingUsers}
                  isLoading={isUsersLoading}
                  teams={teams}
                  searchQuery={searchQuery}
                  assignedUserId={modalAssignedUserId}
                  onUserIdChange={handleModalUserIdChange}
                  onSearchForTeamsClick={handleSearchForTeamsClick}
                  onClearSearchClick={handleClearSearchClick}
                />
              )}
            </ModalColumn>
          </FixaModalBody>
        </FixaSheets>
      </FixaModal>
    </>
  );
};
